@if (link) {
  @switch (link.type) {
    @case (linkTypes.ROUTER) {
      <a
        class="card-container"
        [routerLink]="[link.url, { outlets: link.outlets ?? {} }]"
        [queryParamsHandling]="link.outlets ? 'preserve' : undefined"
      >
        <ng-container *ngTemplateOutlet="cardTemplate"></ng-container>
      </a>
    }
    @case (linkTypes.PROGRAMMATIC) {
      <a class="card-container" (click)="linkAction($event)">
        <ng-container *ngTemplateOutlet="cardTemplate"></ng-container>
      </a>
    }
    @case (linkTypes.EXTERNAL) {
      <a class="card-container" [href]="link.url" [target]="link.target">
        <ng-container *ngTemplateOutlet="cardTemplate"></ng-container>
      </a>
    }
    @case (linkTypes.NONE) {
      <div class="card-container">
        <ng-container *ngTemplateOutlet="cardTemplate"></ng-container>
      </div>
    }
  }
} @else {
  <wb-card interactive [attr.variant]="darkTheme ? 'dark-grey' : null" [ngClass]="{ dark: darkTheme }">
    <figure>
      @if (embargoDates) {
        <mb-info type="danger">
          <div class="embargo-container">
            <div class="embargo-title">
              <div>
                <wb-icon name="bds/exclamation-mark-circle-outline/24"></wb-icon>
                <wb-text size="l" class="embargo-subline">
                  {{ 'EMBARGO_CONFIRM_SUBLINE' | translate }}
                </wb-text>
              </div>
              @if (tag) {
                <wb-tag variant="secondary" theme="dark">
                  @if (tag.icon) {
                    <wb-icon [name]="tag.icon"></wb-icon>
                  }
                  {{ tag.label | translate }}
                </wb-tag>
              }
            </div>
            @for (embargoDate of embargoDates; track embargoDate) {
              <wb-text size="m" static class="embargo-date">
                {{ embargoDate }}
              </wb-text>
            }
          </div>
        </mb-info>
      }
      @if (tag && !embargoDates) {
        <div class="tags">
          <wb-tag [variant]="darkTheme ? 'secondary' : 'tertiary'" theme="light">
            @if (tag.icon) {
              <wb-icon [name]="tag.icon"></wb-icon>
            }
            {{ tag.label | translate }}
          </wb-tag>
          @if (!outdatedLabel) {
            @if (tag.assetCountLabel && assetCount) {
              <wb-text size="s" class="wb-tag-right">{{
                tag.assetCountLabel | translate: { count: assetCount }
              }}</wb-text>
            }
            @if (videoDuration) {
              <wb-text size="s" class="wb-tag-right">{{ videoDuration | durationFormat }}</wb-text>
            }
          }
          @if (outdatedLabel) {
            <wb-tag class="archived-tag" [variant]="darkTheme ? 'secondary' : 'tertiary'" theme="light">
              {{ outdatedLabel | translate }}
            </wb-tag>
          }
        </div>
      }
      @if (assetType !== 'DOCUMENT' && assetType !== 'MEDIA_COLLECTION' && assetType !== 'TECHNICAL_DATA') {
        @if (media && !showImageFallback) {
          <mb-media [media]="media" [hoverplay]="true" [loop]="true" (imageError)="showImageFallback = true"></mb-media>
        }
        @if (!media || showImageFallback) {
          <wb-placeholder theme="dark"></wb-placeholder>
        }
      }
      @if (assetType === 'MEDIA_COLLECTION') {
        <img src="assets/img/media_collection_card.png" alt="Media Collection" />
      }
      @if (assetType === 'DOCUMENT' || assetType === 'TECHNICAL_DATA') {
        <div class="document-img-placeholder">
          <wb-icon name="bds/document/24"></wb-icon>
        </div>
      }
      @if (tag && !embargoDates && outdatedLabel) {
        @if ((tag.assetCountLabel && assetCount) || videoDuration) {
          <div class="media-meta">
            @if (tag.assetCountLabel && assetCount) {
              <wb-text size="s">{{ tag.assetCountLabel | translate: { count: assetCount } }}</wb-text>
            }
            @if (videoDuration) {
              <wb-text size="s">{{ videoDuration | durationFormat }}</wb-text>
            }
          </div>
        }
      }
    </figure>
    <div class="card-content-wrapper">
      <div class="card-content">
        @if (topline) {
          <div class="topline">
            <wb-icon name="{{ toplineIcon }}"></wb-icon>
            <wb-text static size="s"> {{ topline }} </wb-text>
          </div>
        }
        @if (headline) {
          <wb-heading size="xs">{{ headline }}</wb-heading>
        }
        @if (text) {
          <wb-text size="l">{{ text }}</wb-text>
        }
        @if (fuelLabel) {
          <wb-text size="s">{{ fuelLabel }}</wb-text>
        }
      </div>
      <div class="card-footer">
        @if (metaItems?.length) {
          <ul class="meta-items">
            @for (item of metaItems; track item.label) {
              <li class="meta-item" [ngClass]="{ dark: darkTheme }">
                <wb-text size="s">{{ item.label | translate: item }}</wb-text>
              </li>
            }
          </ul>
        }
        @if (actions) {
          <mb-action-button-group
            [actions]="actions"
            [resource]="data"
            [darkTheme]="darkTheme"
          ></mb-action-button-group>
        }
      </div>
    </div>
  </wb-card>
}

<ng-template #cardTemplate>
  <wb-card interactive [attr.variant]="darkTheme ? 'dark-grey' : null" [ngClass]="{ dark: darkTheme }">
    <figure>
      @if (embargoDates) {
        <mb-info type="danger">
          <div class="embargo-container">
            <div class="embargo-title">
              <div>
                <wb-icon name="bds/exclamation-mark-circle-outline/24"></wb-icon>
                <wb-text size="l" class="embargo-subline">
                  {{ 'EMBARGO_CONFIRM_SUBLINE' | translate }}
                </wb-text>
              </div>
              @if (tag) {
                <wb-tag variant="secondary" theme="dark">
                  @if (tag.icon) {
                    <wb-icon [name]="tag.icon"></wb-icon>
                  }
                  {{ tag.label | translate }}
                </wb-tag>
              }
            </div>
            @for (embargoDate of embargoDates; track embargoDate) {
              <wb-text size="m" static class="embargo-date">
                {{ embargoDate }}
              </wb-text>
            }
          </div>
        </mb-info>
      }

      @if (tag && !embargoDates) {
        <div class="tags">
          <wb-tag [variant]="darkTheme ? 'secondary' : 'tertiary'" theme="light">
            @if (tag.icon) {
              <wb-icon [name]="tag.icon"></wb-icon>
            }
            {{ tag.label | translate }}
          </wb-tag>
          @if (!outdatedLabel) {
            @if (tag.assetCountLabel && assetCount) {
              <wb-text size="s" class="wb-tag-right">{{
                tag.assetCountLabel | translate: { count: assetCount }
              }}</wb-text>
            }
            @if (videoDuration) {
              <wb-text size="s" class="wb-tag-right">{{ videoDuration | durationFormat }}</wb-text>
            }
          }
          @if (outdatedLabel) {
            <wb-tag class="archived-tag" [variant]="darkTheme ? 'secondary' : 'tertiary'" theme="light">
              {{ outdatedLabel | translate }}
            </wb-tag>
          }
        </div>
      }

      @if (assetType !== 'DOCUMENT' && assetType !== 'MEDIA_COLLECTION' && assetType !== 'TECHNICAL_DATA') {
        @if (media && !showImageFallback) {
          <mb-media [media]="media" [hoverplay]="true" [loop]="true" (imageError)="showImageFallback = true"></mb-media>
        }
        @if (!media || showImageFallback) {
          <wb-placeholder theme="dark"></wb-placeholder>
        }
      }
      @if (assetType === 'MEDIA_COLLECTION') {
        <img src="assets/img/media_collection_card.png" alt="Media Collection" />
      }
      @if (assetType === 'DOCUMENT' || assetType === 'TECHNICAL_DATA') {
        <div class="document-img-placeholder">
          <wb-icon name="bds/document/24"></wb-icon>
        </div>
      }
      @if (tag && !embargoDates && outdatedLabel) {
        @if ((tag.assetCountLabel && assetCount) || videoDuration) {
          <div class="media-meta">
            @if (tag.assetCountLabel && assetCount) {
              <wb-text size="s">{{ tag.assetCountLabel | translate: { count: assetCount } }}</wb-text>
            }
            @if (videoDuration) {
              <wb-text size="s">{{ videoDuration | durationFormat }}</wb-text>
            }
          </div>
        }
      }
    </figure>

    <div class="card-content-wrapper">
      <div class="card-content">
        @if (topline) {
          <div class="topline">
            <wb-icon name="{{ toplineIcon }}"></wb-icon>
            <wb-text static size="s"> {{ topline }} </wb-text>
          </div>
        }
        @if (headline) {
          <wb-heading size="xs">{{ headline }}</wb-heading>
        }
        @if (text) {
          <wb-text size="l">{{ text }}</wb-text>
        }
        @if (fuelLabel) {
          <wb-text size="s">{{ fuelLabel }}</wb-text>
        }
      </div>

      <div class="card-footer">
        @if (metaItems?.length) {
          <ul class="meta-items">
            @for (item of metaItems; track item.label) {
              <li class="meta-item" [ngClass]="{ dark: darkTheme }">
                <wb-text size="s">{{ item.label | translate: item }}</wb-text>
              </li>
            }
          </ul>
        }
        @if (actions) {
          <mb-action-button-group
            [actions]="actions"
            [resource]="data"
            [darkTheme]="darkTheme"
          ></mb-action-button-group>
        }
      </div>
    </div>
  </wb-card>
</ng-template>
